import styled from "@emotion/styled"
import { css } from "@emotion/react"
import getMediaQuery from "css/breakpoints"
import Image from "components/media/ImageCLD"
import Link from "components/link/Link"
import { body50, legend100, legend80, bold, light } from "css/text"
import { contrastMode } from "css/a11y"
import { LabelFont } from "../watch-card/WatchCard"
import { useDictionary } from "contexts/Dictionary"
import { useNavigation } from "contexts/Navigation"
import { useConsole } from "contexts/Console"
import XplitCTA from "components/xplit/XplitCTA"

const Item = styled.div`
  position: relative;
  grid-column: span 2;
  overflow: hidden;
  height: 100%;

  ${getMediaQuery("m")} {
    grid-column: unset;
  }

  &.dark-theme {
    color: rgb(var(--pure-white));
  }

  &.light-theme {
    color: rgb(var(--light-black));
  }

  * {
    pointer-events: none;
  }

  a,
  button {
    pointer-events: auto;
  }

  ${props =>
    props.XplitCTA &&
    css`
      cursor: pointer;
    `}
`
const buttonkStyles = css`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template: "container";
  color: inherit;
`
const RealLink = styled.a`
  ${buttonkStyles}
  text-decoration: none;
`
const Figure = styled.figure`
  grid-area: container;

  img {
    display: block;
    width: 100%;
  }
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: container;
  place-self: end start;
  box-sizing: border-box;
  padding-inline: 3vw;
  padding-block: 0 3vw;
  width: 100%;
  z-index: 1;

  [dir="rtl"] & {
    place-self: end end;
    width: fit-content;

    ${getMediaQuery("xl")} {
      padding-inline: 3vw;
      padding-block: 0 3vw;
    }
  }

  ${getMediaQuery("xl")} {
    padding-inline: 2vw 10vw;
    padding-block: 0 2vw;
  }

  span.fake-link {
    padding: 0;
  }

  ${props =>
    props.XplitCTA &&
    css`
      align-items: end;
      flex-direction: row;
      justify-content: space-between;

      ${getMediaQuery("xl")} {
        padding-inline: 2vw;
        padding-block: 0 2vw;
      }

      hgroup {
        max-width: 80%;
      }

      h2 {
        margin-block-end: 0;
      }
    `}
`

const HGroup = styled.hgroup`
  position: relative;
  ${contrastMode}
`
const Label = styled.span`
  ${LabelFont}
  margin-block-end: 5px;
`
const Kicker = styled.p`
  ${body50}
  ${bold}
  line-height: 1.1;

  .panel-open & {
    ${legend100}
  }
`
const Title = styled.h2`
  ${legend100}
  ${light}
  line-height: 1.1;
  margin-block: 5px 20px;
  text-align: start;

  .panel-open & {
    ${legend80}
  }

  ${getMediaQuery("l")} {
    margin-block: 10px 30px;
  }
`
const SmallLink = styled(Link)`
  &.inline.green {
    ${legend100}
    ${bold};

    .panel-open & {
      ${legend80}
    }

    svg {
      width: 10px;
      height: 10px;
      margin-inline: 0.25rem !important;
    }
  }
`

export default function EditoCard({ kicker, headingTitle, image_cld: image, link = {}, theme, new_model, cta = [] }) {
  const dictionary = useDictionary()
  const navigation = useNavigation()
  const console = useConsole()

  const sizes = { sizes: "(min-width: 80rem) 33vw, (min-width: 64.0625rem) 50vw, 100vw" }

  function onCTAClick(e) {
    e.preventDefault()

    link.href ? navigation.navigate(e.currentTarget.getAttribute("href")) : window.open(link.external, "_blank")
  }

  function onXplitCTAClick(e) {
    if (e.target.tagName === "BUTTON") return
    const linkEl = e.target.getElementsByTagName("button")
    if (linkEl.length > 0) {
      linkEl[0].click()
    }
  }

  function generateImage() {
    return (
      <Figure>
        <Image {...{ ...image, ...sizes }} />
      </Figure>
    )
  }

  const href = link.href ? navigation.localize(link.href) : link.external

  const xplit_button = cta[0]?.xplit_button

  if (xplit_button) {
    return (
      <Item className={theme} onClick={onXplitCTAClick} XplitCTA>
        <RealLink as='div'>
          {generateImage()}

          <Content XplitCTA>
            <HGroup>
              {kicker && <Kicker dangerouslySetInnerHTML={{ __html: kicker }} />}
              <Title>{headingTitle}</Title>
            </HGroup>

            <XplitCTA {...{ style: "icon translucent-dark" }}>
              <span>{xplit_button.label}</span>
            </XplitCTA>
          </Content>
        </RealLink>
      </Item>
    )
  }

  return (
    <Item className={theme}>
      <RealLink href={href} onClick={onCTAClick} aria-label={link.aria_label}>
        {generateImage()}

        <Content>
          <HGroup>
            {new_model && <Label>{dictionary.newModel()}</Label>}
            {kicker && <Kicker dangerouslySetInnerHTML={{ __html: kicker }} />}
            <Title>{headingTitle}</Title>
          </HGroup>

          <SmallLink className='inline green' external={link.external}>
            {link.label}
          </SmallLink>
        </Content>
      </RealLink>
    </Item>
  )
}
